import axiosInstance from '../../axiosInstance';

const BASE = '/api/hcp/signup';

export async function postGeneralInfoDraft(
  values,
  idImageFile,
  draftId,
  validationConfig
) {
  const fd = new FormData();

  if (draftId != null) fd.append('draftId', String(draftId));

  fd.append('firstName', values.firstName || '');
  fd.append('lastName', values.lastName || '');
  fd.append('email', values.email || '');
  fd.append(
    'categoryId',
    values.categoryId != null && String(values.categoryId).trim() !== ''
      ? String(values.categoryId).trim()
      : ''
  );
  fd.append('dateOfBirth', values.dateOfBirth || '');
  fd.append('nationality', values.nationality || '');
  fd.append('idNumber', values.idNumber || '');
  fd.append('countryCode', values.countryCode || '');
  fd.append('phoneNumber', String(values.phoneNumber || '').replace(/\D/g, ''));
  fd.append('gender', values.gender || '');
  fd.append('addressCountry', values.addressCountry || '');
  fd.append('addressState', values.addressState || '');
  fd.append('addressCity', values.addressCity || '');
  fd.append('addressStreet', values.addressStreet || '');
  fd.append('addressZipCode', values.addressZipCode || '');
  fd.append('validationConfig', JSON.stringify(validationConfig || {}));

  const pwd = values.password != null ? String(values.password).trim() : '';
  if (pwd) {
    fd.append('password', values.password);
    fd.append('confirmPassword', values.confirmPassword || '');
  }

  if (idImageFile instanceof File) {
    fd.append('id_image', idImageFile);
  }

  fd.append('step', '1');

  const res = await axiosInstance.post(`${BASE}/save-step`, fd, {
    withCredentials: true,
  });
  return res.data;
}

export async function getSignupDraft(id) {
  const res = await axiosInstance.get(`${BASE}/draft`, {
    params: { draftId: id },
    withCredentials: true,
  });
  return res.data;
}

export const HCP_SIGNUP_DRAFT_STORAGE_KEY = 'hcp_signup_draft_id';

/**
 * @param {number} draftId
 * @param {object[]} licensesRows – form rows (camelCase; may include _rowKey)
 * @param {object} licenseFieldConfig optional config JSON for server
 */
export async function postLicensesDraft(
  draftId,
  licensesRows,
  licenseFieldConfig
) {
  const fd = new FormData();
  fd.append('draftId', String(draftId));

  const payload = licensesRows.map((r) => ({
    prefix: r.prefix || '',
    licenseNumber: r.licenseNumber || '',
    issuingCountry: r.issuingCountry || '',
    authorityName: r.authorityName || '',
    issueDate: r.issueDate || '',
    expiryDate: r.expiryDate || '',
    existingAttachmentUploadId: r.existingAttachmentUploadId ?? null,
  }));

  fd.append('licenses', JSON.stringify(payload));
  fd.append(
    'licenseConfig',
    JSON.stringify(
      licenseFieldConfig && typeof licenseFieldConfig === 'object'
        ? licenseFieldConfig
        : {}
    )
  );

  licensesRows.forEach((row, i) => {
    if (row.attachmentFile instanceof File) {
      fd.append(`attachment_${i}`, row.attachmentFile);
    }
  });

  fd.append('step', '2');

  const res = await axiosInstance.post(`${BASE}/save-step`, fd, {
    withCredentials: true,
  });
  return res.data;
}

/** @typedef {{ countryOfCertificate?: string, degreeName?: string, institutionName?: string, authorityName?: string, fieldOfStudy?: string, graduationDate?: string, studyYears?: number, accreditationStatus?: string, accreditingAuthority?: string, accreditationFile?: File|null, existingAccreditationDocumentUploadId?: number|null }} EduRow */

/** @typedef {{ internshipTraining?: string, postGraduationInternship?: string, internshipCompletion?: string, internshipStart?: string, internshipEnd?: string, internshipCertificateFile?: File|null, existingInternshipCertificateUploadId?: number|null, additionalExperience?: string, additionalExperienceStart?: string, additionalExperienceEnd?: string, additionalExperienceCertificateFile?: File|null, existingAdditionalExperienceCertificateUploadId?: number|null, undergraduateInternship?: string, undergraduateInternshipStart?: string, undergraduateInternshipEnd?: string, undergraduateInternshipCertificateFile?: File|null, existingUndergraduateInternshipCertificateUploadId?: number|null, relevantAttachmentFiles?: File[], existingRelevantAttachmentIds?: number[] }} ExpShape */

/**
 * @param {number} draftId
 * @param {{ educations: EduRow[], experience: ExpShape }} values
 */
export async function postEducationExperienceDraft(draftId, values) {
  const fd = new FormData();
  fd.append('draftId', String(draftId));

  const educationsPayload = (values.educations || []).map((r) => ({
    countryOfCertificate: r.countryOfCertificate ?? '',
    degreeName: r.degreeName ?? '',
    institutionName: r.institutionName ?? '',
    authorityName: r.authorityName ?? '',
    fieldOfStudy: r.fieldOfStudy ?? '',
    graduationDate: r.graduationDate ?? '',
    studyYears:
      typeof r.studyYears === 'number' && Number.isFinite(r.studyYears)
        ? r.studyYears
        : Number(r.studyYears),
    accreditationStatus: r.accreditationStatus ?? '',
    accreditingAuthority: r.accreditingAuthority ?? '',
    existingAccreditationDocumentUploadId:
      r.existingAccreditationDocumentUploadId ?? null,
  }));

  fd.append('educations', JSON.stringify(educationsPayload));

  const ex = values.experience || {};

  fd.append(
    'experience',
    JSON.stringify({
      internshipTraining: ex.internshipTraining ?? '',
      postGraduationInternship: ex.postGraduationInternship ?? '',
      internshipCompletion: ex.internshipCompletion ?? '',
      internshipStart: ex.internshipStart ?? '',
      internshipEnd: ex.internshipEnd ?? '',
      existingInternshipCertificateUploadId:
        ex.existingInternshipCertificateUploadId ?? null,
      additionalExperience: ex.additionalExperience ?? '',
      additionalExperienceStart: ex.additionalExperienceStart ?? '',
      additionalExperienceEnd: ex.additionalExperienceEnd ?? '',
      existingAdditionalExperienceCertificateUploadId:
        ex.existingAdditionalExperienceCertificateUploadId ?? null,
      undergraduateInternship: ex.undergraduateInternship ?? '',
      undergraduateInternshipStart: ex.undergraduateInternshipStart ?? '',
      undergraduateInternshipEnd: ex.undergraduateInternshipEnd ?? '',
      existingUndergraduateInternshipCertificateUploadId:
        ex.existingUndergraduateInternshipCertificateUploadId ?? null,
      existingRelevantAttachmentIds: Array.isArray(
        ex.existingRelevantAttachmentIds
      )
        ? ex.existingRelevantAttachmentIds
        : [],
    })
  );

  (values.educations || []).forEach((row, i) => {
    if (row.accreditationFile instanceof File) {
      fd.append(`accreditation_doc_${i}`, row.accreditationFile);
    }
  });

  if (ex.internshipCertificateFile instanceof File) {
    fd.append('internship_certificate', ex.internshipCertificateFile);
  }
  if (ex.additionalExperienceCertificateFile instanceof File) {
    fd.append(
      'additional_experience_certificate',
      ex.additionalExperienceCertificateFile
    );
  }
  if (ex.undergraduateInternshipCertificateFile instanceof File) {
    fd.append(
      'undergraduate_internship_certificate',
      ex.undergraduateInternshipCertificateFile
    );
  }

  (ex.relevantAttachmentFiles || []).forEach((file, j) => {
    if (file instanceof File) fd.append(`experience_attachment_${j}`, file);
  });

  fd.append('step', '3');

  const res = await axiosInstance.post(`${BASE}/save-step`, fd, {
    withCredentials: true,
  });
  return res.data;
}

/**
 * Finalize sign-up: creates HCP profile and public HCP id (server validates Steps 1–3).
 * @param {number} draftId
 */
export async function postSignupComplete(draftId) {
  const res = await axiosInstance.post(
    `${BASE}/complete`,
    { draftId },
    { withCredentials: true }
  );
  return res.data;
}
